import { isValidIBAN } from "ibantools";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { DEBOUNCE_VALUE } from "src/constants";
import { useGetIbanValidationQuery } from "src/graphql/generated/queries";
import { getSpecificError } from "src/utils";

// @note: source https://en.wikipedia.org/wiki/International_Bank_Account_Number
// shorter iban : Norway - 15 char (18 with spaces)
const MIN_LENGTH_IBAN = 15;

type IbanInfos = { bic: string } | { error: string };
interface Props {
  rawIban: string;
  error?: string;
  setError(message: string): void;
  clearError(): void;
  allowForeignIban?: boolean;
}

export const useIbanCheck = ({
  rawIban = "",
  error = "",
  setError,
  clearError,
  allowForeignIban = true,
}: Props) => {
  const [testedIBANs, setTestedIBANs] = useState<{ [key: string]: IbanInfos }>({});

  const iban = rawIban.replace(/ /g, "").toUpperCase();
  const ibanInfos = testedIBANs[iban];

  const bic = ibanInfos && "bic" in ibanInfos ? ibanInfos.bic : "";
  const ibanCheckError = ibanInfos && "error" in ibanInfos ? ibanInfos.error : "";

  useEffect(() => {
    if (bic && error) {clearError();}
  }, [bic, error]);

  useEffect(() => {
    if (ibanCheckError && ibanCheckError !== error) {
      setError(ibanCheckError);
    }
  }, [ibanCheckError, error]);

  const { ...ibanCheckQueryInfos } = useGetIbanValidationQuery(
    {
      iban,
      allowForeignIban,
    },
    {
      enabled: isValidIBAN(iban),
      onSuccess: (data) => {
        setTestedIBANs((currentState) => ({
          ...currentState,
          [iban]: data.ibanValidation.bic
            ? {
                bic: data.ibanValidation.bic,
              }
            : { error: "IBAN invalide, exemple : FRXX XXXX XXXX XXXX XXXX XXXX XXX" },
        }));
      },
      onError: (error) => {
        setTestedIBANs((currentState) => ({
          ...currentState,
          [iban]: {
            error: getSpecificError(error, "IBAN non vérifié"),
          },
        }));
      },
    }
  );

  const checkIban = () => {
    if (!iban.length || bic) {return;}

    if (iban.length < MIN_LENGTH_IBAN) {
      setTestedIBANs((currentState) => ({
        ...currentState,
        [iban]: {
          error: "Iban trop court",
        },
      }));
      return;
    }

    if (!allowForeignIban && iban && !iban.startsWith("FR")) {
      setTestedIBANs((currentState) => ({
        ...currentState,
        [iban]: {
          error:
            "Vous ne pouvez pas mettre à jour votre IBAN vers un IBAN étranger depuis votre espace client, veuillez contacter votre agence directement.",
        },
      }));
      return;
    }

    setTestedIBANs((currentState) => ({
      ...currentState,
      [iban]: {
        error: "Iban invalide, exemple : FRXX XXXX XXXX XXXX XXXX XXXX XXX",
      },
    }));
  };

  const debouncedCheckIban = useDebouncedCallback(checkIban, DEBOUNCE_VALUE);

  useEffect(() => {
    debouncedCheckIban();
  }, [iban]);

  return {
    iban,
    bic,
    ibanCheckLoading: ibanCheckQueryInfos.isInitialLoading || ibanCheckQueryInfos.isRefetching,
  };
};
